import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Send, MessageSquare, Mail, AlertCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { DirectionBadge, CommBodyCell } from "@/components/comms/DirectionBadge";

export default function ClientCommunicationTab({ client, onCommSent }) {
    const [comms, setComms] = useState([]);
    const [smsTemplates, setSmsTemplates] = useState([]);
    const [emailTemplates, setEmailTemplates] = useState([]);
    const [smsTpl, setSmsTpl] = useState("");
    const [emailTpl, setEmailTpl] = useState("");
    const [confirm, setConfirm] = useState(null); // { kind, label, recipient }
    const [busy, setBusy] = useState(false);

    const loadComms = useCallback(async () => {
        const r = await api.get(`/clients/${client.id}/communications`);
        setComms(r.data || []);
    }, [client.id]);

    const loadTemplates = useCallback(async () => {
        const r = await api.get("/message-templates");
        const list = Array.isArray(r.data) ? r.data : (r.data.items || []);
        const actives = list.filter(t => t.active !== false);
        setSmsTemplates(actives.filter(t => t.channel === "sms"));
        setEmailTemplates(actives.filter(t => t.channel === "email"));
    }, []);

    useEffect(() => { loadComms(); loadTemplates(); }, [loadComms, loadTemplates]);

    const renderPreview = (body) => {
        if (!body) return "";
        return body
            .replace(/\{\{\s*first_name\s*\}\}/gi, client.first_name || "")
            .replace(/\{\{\s*last_name\s*\}\}/gi, client.last_name || "")
            .replace(/\{\{\s*case_id\s*\}\}/gi, client.case_id || "")
            .replace(/\{\{\s*client_name\s*\}\}/gi, client.client_name || "");
    };

    const tplObj = (id, list) => list.find(t => t.id === id);
    const phone = client.phone_e164 || client.phone_raw;
    const email = client.email;
    const canSms = !!phone && !!smsTpl;
    const canEmail = !!email && !!emailTpl;

    const requestSend = (kind) => {
        if (kind === "sms") setConfirm({ kind: "sms", label: tplObj(smsTpl, smsTemplates)?.template_name || tplObj(smsTpl, smsTemplates)?.template_key, recipient: phone });
        else setConfirm({ kind: "email", label: tplObj(emailTpl, emailTemplates)?.template_name || tplObj(emailTpl, emailTemplates)?.template_key, recipient: email });
    };

    const doSend = async () => {
        if (!confirm) return;
        setBusy(true);
        try {
            const endpoint = confirm.kind === "sms" ? "send-sms" : "send-email";
            const tplId = confirm.kind === "sms" ? smsTpl : emailTpl;
            const r = await api.post(`/clients/${client.id}/${endpoint}`, { template_id: tplId });
            if (r.data.ok) toast.success(`${confirm.kind === "sms" ? "SMS" : "Email"} sent (${r.data.status})`);
            else toast.error(`${confirm.kind === "sms" ? "SMS" : "Email"} failed (${r.data.status})`);
            setConfirm(null);
            loadComms();
            onCommSent && onCommSent();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    const smsPreview = smsTpl ? renderPreview(tplObj(smsTpl, smsTemplates)?.body) : "";
    const emailTplDoc = emailTpl ? tplObj(emailTpl, emailTemplates) : null;

    return (
        <div className="space-y-4" data-testid="client-comm-tab">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Send SMS */}
                <div className="bg-white border border-border rounded-xl p-5 shadow-sm">
                    <div className="flex items-center gap-2 mb-3">
                        <MessageSquare className="h-4 w-4 text-primary" />
                        <div className="text-sm font-medium text-gray-900">Send SMS</div>
                    </div>
                    {!phone && (
                        <div className="flex items-start gap-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2 mb-3" data-testid="sms-no-phone-warning">
                            <AlertCircle className="h-4 w-4 mt-0.5" />
                            <div>Client has no valid phone. Edit Contact Info to add one before sending.</div>
                        </div>
                    )}
                    {client.do_not_text && (
                        <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 mb-3" data-testid="dnt-warning">
                            <AlertCircle className="h-4 w-4 mt-0.5" />
                            <div>This client is marked <strong>Do Not Text</strong>.</div>
                        </div>
                    )}
                    <div className="space-y-2">
                        <label className="text-xs font-medium text-gray-700">Active SMS Template</label>
                        <Select value={smsTpl} onValueChange={setSmsTpl}>
                            <SelectTrigger data-testid="sms-template-select"><SelectValue placeholder="Select a template…" /></SelectTrigger>
                            <SelectContent>
                                {smsTemplates.length === 0
                                    ? <SelectItem value="__none__" disabled>No active SMS templates</SelectItem>
                                    : smsTemplates.map(t => <SelectItem key={t.id} value={t.id}>{t.template_name || t.template_key}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        {smsTpl && (
                            <Textarea readOnly value={smsPreview} rows={4} className="text-xs font-mono bg-gray-50" data-testid="sms-preview" />
                        )}
                        <div className="flex justify-end">
                            <Button disabled={!canSms || busy} onClick={() => requestSend("sms")} data-testid="send-sms-button">
                                <Send className="h-4 w-4 mr-1" /> Send SMS
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Send Email */}
                <div className="bg-white border border-border rounded-xl p-5 shadow-sm">
                    <div className="flex items-center gap-2 mb-3">
                        <Mail className="h-4 w-4 text-primary" />
                        <div className="text-sm font-medium text-gray-900">Send Email</div>
                    </div>
                    {!email && (
                        <div className="flex items-start gap-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2 mb-3" data-testid="email-no-address-warning">
                            <AlertCircle className="h-4 w-4 mt-0.5" />
                            <div>Client has no valid email. Edit Contact Info to add one before sending.</div>
                        </div>
                    )}
                    {client.do_not_email && (
                        <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 mb-3" data-testid="dne-warning">
                            <AlertCircle className="h-4 w-4 mt-0.5" />
                            <div>This client is marked <strong>Do Not Email</strong>.</div>
                        </div>
                    )}
                    <div className="space-y-2">
                        <label className="text-xs font-medium text-gray-700">Active Email Template</label>
                        <Select value={emailTpl} onValueChange={setEmailTpl}>
                            <SelectTrigger data-testid="email-template-select"><SelectValue placeholder="Select a template…" /></SelectTrigger>
                            <SelectContent>
                                {emailTemplates.length === 0
                                    ? <SelectItem value="__none__" disabled>No active Email templates</SelectItem>
                                    : emailTemplates.map(t => <SelectItem key={t.id} value={t.id}>{t.template_name || t.template_key}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        {emailTplDoc && (
                            <div className="text-xs bg-gray-50 border border-border rounded p-2 space-y-1" data-testid="email-template-preview">
                                <div><span className="text-muted-foreground">Key:</span> <span className="font-mono">{emailTplDoc.template_key}</span></div>
                                <div><span className="text-muted-foreground">SendGrid Template ID:</span> <span className="font-mono">{emailTplDoc.sendgrid_template_id || "—"}</span></div>
                                <div><span className="text-muted-foreground">Subject:</span> {emailTplDoc.subject || "—"}</div>
                                <div className="text-muted-foreground italic mt-1">Dynamic data: first_name, last_name, case_id, client_name</div>
                            </div>
                        )}
                        <div className="flex justify-end">
                            <Button disabled={!canEmail || busy} onClick={() => requestSend("email")} data-testid="send-email-button">
                                <Send className="h-4 w-4 mr-1" /> Send Email
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Communication history */}
            <div className="bg-white border border-border rounded-xl overflow-hidden shadow-sm">
                <div className="px-5 py-3 border-b border-border flex items-center justify-between">
                    <div className="text-sm font-medium text-gray-900">Communication History</div>
                    <div className="text-xs text-muted-foreground">{comms.length} record{comms.length === 1 ? "" : "s"}</div>
                </div>
                {comms.length === 0 ? (
                    <div className="p-5 text-center text-muted-foreground">
                        <div className="text-sm font-medium text-gray-700">No communication history yet.</div>
                        <div className="text-xs mt-1">SMS and Email sends linked to this client will appear here.</div>
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-muted-foreground">
                            <tr>
                                <th className="px-3 py-2">Date</th>
                                <th className="px-3 py-2">Channel</th>
                                <th className="px-3 py-2">Dir</th>
                                <th className="px-3 py-2">Campaign</th>
                                <th className="px-3 py-2">Template</th>
                                <th className="px-3 py-2">Status</th>
                                <th className="px-3 py-2">Provider</th>
                                <th className="px-3 py-2">Provider ID</th>
                                <th className="px-3 py-2">Error</th>
                                <th className="px-3 py-2">Preview</th>
                            </tr>
                        </thead>
                        <tbody>
                            {comms.map(c => (
                                <tr key={c.id} className="border-t border-border" data-testid={`client-comm-row-${c.id}`}>
                                    <td className="px-3 py-2 text-xs">{c.created_at ? new Date(c.created_at).toLocaleString() : "—"}</td>
                                    <td className="px-3 py-2 capitalize">{c.channel}</td>
                                    <td className="px-3 py-2"><DirectionBadge comm={c} testId={`client-comm-direction-${c.id}`} /></td>
                                    <td className="px-3 py-2 text-xs">
                                        {c.campaign_id
                                            ? <Link to={`/campaigns/${c.campaign_id}`} className="text-primary hover:underline">{c.campaign_name || c.campaign_id.slice(0, 8)}</Link>
                                            : <span className="italic text-muted-foreground">One-off</span>}
                                    </td>
                                    <td className="px-3 py-2 text-xs">{c.template_key || c.template_name || "—"}</td>
                                    <td className="px-3 py-2 capitalize text-xs">{c.status}</td>
                                    <td className="px-3 py-2 text-xs">{c.provider_status || "—"}</td>
                                    <td className="px-3 py-2 text-xs font-mono">{c.provider_message_sid || c.provider_message_id || "—"}</td>
                                    <td className="px-3 py-2 text-xs">{c.error_code || "—"}</td>
                                    <td className="px-3 py-2 align-top"><CommBodyCell comm={c} testId={`client-comm-body-${c.id}`} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            <AlertDialog open={!!confirm} onOpenChange={(o) => { if (!o) setConfirm(null); }}>
                <AlertDialogContent data-testid="send-confirm-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{confirm?.kind === "sms" ? "Send this SMS" : "Send this email"} to {client.client_name || `${client.first_name} ${client.last_name}`}?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Recipient: <span className="font-mono">{confirm?.recipient}</span><br />
                            Template: <span className="font-medium">{confirm?.label || "—"}</span>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={doSend} disabled={busy} data-testid="send-confirm-action">{busy ? "Sending…" : "Send"}</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
